import { AccountMediaKind } from '../models/winery-account';

type ProcessedImage = {
  blob: Blob;
  fileName: string;
  contentType: string;
  width: number;
  height: number;
};

const DARK_LOGO_BACKGROUND = '#FFFFFF';
const LIGHT_LOGO_BACKGROUND = '#1F1F1F';
const LOGO_TRANSPARENCY_ALPHA_THRESHOLD = 24;
const LOGO_VISIBLE_ALPHA_THRESHOLD = 32;
const LOGO_TRANSPARENCY_MIN_RATIO = 0.08;
const LOGO_DARK_LUMINANCE_THRESHOLD = 0.3;
const LOGO_LIGHT_LUMINANCE_THRESHOLD = 0.72;
const LOGO_CONTRAST_DECISION_RATIO = 1.05;
const DARK_BACKGROUND_LUMINANCE = 0.013702083047289686;

const EXTENSION_BY_MIME: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/avif': 'avif'
};

const loadImageFromFile = (file: File): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Unable to read image.'));
      img.src = reader.result as string;
    };
    reader.onerror = () => reject(new Error('Unable to read image.'));
    reader.readAsDataURL(file);
  });

const getMimeExtension = (mime: string) => EXTENSION_BY_MIME[mime] ?? 'jpg';

const toRelativeLuminance = (red: number, green: number, blue: number): number => {
  const toLinearChannel = (value: number) => {
    const normalized = value / 255;
    return normalized <= 0.04045 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
  };

  return (
    0.2126 * toLinearChannel(red) +
    0.7152 * toLinearChannel(green) +
    0.0722 * toLinearChannel(blue)
  );
};

const contrastRatio = (left: number, right: number): number => {
  const lighter = Math.max(left, right);
  const darker = Math.min(left, right);
  return (lighter + 0.05) / (darker + 0.05);
};

const detectLogoBackgroundColor = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number
): string | null => {
  const { data } = ctx.getImageData(0, 0, width, height);
  const totalPixels = width * height;
  let transparentPixels = 0;
  let visibleWeight = 0;
  let luminanceSum = 0;
  let contrastOnWhiteSum = 0;
  let contrastOnDarkSum = 0;

  for (let index = 0; index < data.length; index += 4) {
    const red = data[index] ?? 0;
    const green = data[index + 1] ?? 0;
    const blue = data[index + 2] ?? 0;
    const alphaByte = data[index + 3] ?? 0;

    if (alphaByte <= LOGO_TRANSPARENCY_ALPHA_THRESHOLD) {
      transparentPixels += 1;
    }

    if (alphaByte <= LOGO_VISIBLE_ALPHA_THRESHOLD) {
      continue;
    }

    const alpha = alphaByte / 255;
    const luminance = toRelativeLuminance(red, green, blue);
    visibleWeight += alpha;
    luminanceSum += luminance * alpha;
    contrastOnWhiteSum += contrastRatio(luminance, 1) * alpha;
    contrastOnDarkSum += contrastRatio(luminance, DARK_BACKGROUND_LUMINANCE) * alpha;
  }

  if (!visibleWeight) {
    return null;
  }

  const transparencyRatio = transparentPixels / totalPixels;
  if (transparencyRatio < LOGO_TRANSPARENCY_MIN_RATIO) {
    return null;
  }

  const averageContrastOnWhite = contrastOnWhiteSum / visibleWeight;
  const averageContrastOnDark = contrastOnDarkSum / visibleWeight;
  if (averageContrastOnDark >= averageContrastOnWhite * LOGO_CONTRAST_DECISION_RATIO) {
    return LIGHT_LOGO_BACKGROUND;
  }
  if (averageContrastOnWhite >= averageContrastOnDark * LOGO_CONTRAST_DECISION_RATIO) {
    return DARK_LOGO_BACKGROUND;
  }

  const averageLuminance = luminanceSum / visibleWeight;
  if (averageLuminance <= LOGO_DARK_LUMINANCE_THRESHOLD) {
    return DARK_LOGO_BACKGROUND;
  }
  if (averageLuminance >= LOGO_LIGHT_LUMINANCE_THRESHOLD) {
    return LIGHT_LOGO_BACKGROUND;
  }

  return null;
};

export const resizeImageForKind = async (kind: AccountMediaKind, file: File): Promise<ProcessedImage> => {
  const img = await loadImageFromFile(file);
  const isLogo = kind === 'logo';
  const maxWidth = isLogo ? 512 : 1920;
  const maxHeight = isLogo ? 512 : 1080;

  const scale = Math.min(1, maxWidth / img.width, maxHeight / img.height);
  const targetWidth = Math.max(1, Math.round(img.width * scale));
  const targetHeight = Math.max(1, Math.round(img.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = targetWidth;
  canvas.height = targetHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    throw new Error('Unable to process image.');
  }
  ctx.drawImage(img, 0, 0, targetWidth, targetHeight);

  if (isLogo) {
    const logoBackgroundColor = detectLogoBackgroundColor(ctx, targetWidth, targetHeight);
    if (logoBackgroundColor) {
      ctx.clearRect(0, 0, targetWidth, targetHeight);
      ctx.fillStyle = logoBackgroundColor;
      ctx.fillRect(0, 0, targetWidth, targetHeight);
      ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
    }
  }

  const contentType =
    file.type && file.type.startsWith('image/') ? file.type : isLogo ? 'image/png' : 'image/jpeg';

  const blob = await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (result) => {
        if (result) {
          resolve(result);
        } else {
          reject(new Error('Unable to process image.'));
        }
      },
      contentType,
      contentType === 'image/jpeg' ? 0.92 : 0.95
    );
  });

  const baseName = file.name?.split('.').slice(0, -1).join('.') || kind;
  const extension = getMimeExtension(contentType);
  const fileName = `${baseName}-${targetWidth}x${targetHeight}.${extension}`;

  return {
    blob,
    fileName,
    contentType,
    width: targetWidth,
    height: targetHeight
  };
};
